"use client";
import {
  Checkbox,
  Text,
  Flex,
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Button,
  AccordionPanel,
  Input,
  RadioGroup,
  Radio,
  Stack,
  useToast,
  Spinner,
  AspectRatio,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton 
} from "@chakra-ui/react";
import { useEffect } from "react";
import { Dropzone, Jumbotron, GoogleForm } from "components";
import { MinusIcon, AddIcon } from "@chakra-ui/icons";
import { useState, useRef } from "react";
import { POST_TRAIN_USER_MODEL, POST_TRAIN_USER_MODEL_FLUX, GET_USER_MODEL_BY_ID, GET_USER_MODELS } from "api";
import { useQueryClient } from "@tanstack/react-query";
import { fetch } from "libs";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAppContext } from "hooks";
import { useGetUserModelsNames } from "queries";

declare const gtag: Function;
type APIError = {
  detail: string;
  name?: string;
};
export const CreateYourModel = () => {
  const [files, setFiles] = useState<any[]>([]);
  const [modelName, setModelName] = useState("");
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useAppContext();
  const toast = useToast();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const router = useRouter();
  const modelNameRef = useRef<HTMLInputElement>(null);
  const [isModelNameInvalid, setIsModelNameInvalid] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [modelCategoryName, setModelCategoryName] = useState<string>("woman");
  const [tuneId, setTuneId] = useState<string>("1104607");
  const { data: userModelsNames } = useGetUserModelsNames();
  const [useFlux, setUseFlux] = useState(false);
  const [progress, setProgress] = useState(0); // Progress percentage state
  const [showProgressBar, setShowProgressBar] = useState(false); // State to show progress bar
  const [isTraining, setIsTraining] = useState(false); // Track if FLUX training is active
  const [showBlockingModal, setShowBlockingModal] = useState(false); // Show popup on navigation attempts
  
  useEffect(() => {
    const handleNavigationAttempt = (e: MouseEvent) => {
      // Allow modal close button clicks
      const target = e.target as HTMLElement;
  
      // Check if the click is on the ModalCloseButton or inside the modal content
      if (target.closest(".chakra-modal__close-btn") || target.closest(".chakra-modal__content")) {
        return; // Allow clicks inside the modal or on the close button
      }
  
      // Block navigation clicks during training
      if (isTraining) {
        e.preventDefault();
        setShowBlockingModal(true);
      }
    };
  
    document.addEventListener("click", handleNavigationAttempt, true); // 'true' for capture phase
  
    return () => {
      document.removeEventListener("click", handleNavigationAttempt, true);
    };
  }, [isTraining]);
  
  const pollStatusByModelUsername = async (username: string): Promise<"completed" | "pending" | "not_found"> => {
    try {
      const data = await fetch(GET_USER_MODELS); // ✅ returns already-parsed JSON

  
      if (!data.models || !Array.isArray(data.models)) return "not_found";
  
      const matchedModel = data.models
  .filter((model: any) => model.username === username)
  .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  
      return matchedModel?.statusFlux || "not_found";
    } catch (err) {
      console.error("Error polling model status:", err);
      return "not_found";
    }
  };
  
  

  const handleModelSubmit = async () => {

      // Normalize the model name
    const normalizedModelName = modelName.toLowerCase().replace(/[^a-z0-9]/g, "");

    if (!profile) {
      setIsLoginModalOpen(true);
      return;
    }
  
    const validNamePattern = /^[a-zA-Z0-9]+$/;
    if (!modelName) {
      toast({
        title: "Model Name Required",
        description: "Your model name cannot be empty",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      setIsModelNameInvalid(true);
      return;
    } else if (!validNamePattern.test(modelName)) {
      toast({
        title: "Invalid Model Name",
        description: "Model name can only contain letters and numbers, no spaces or special characters.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsModelNameInvalid(true);
      return;
    } else if (userModelsNames?.includes(normalizedModelName)) {
    // Check for existing model name
    toast({
      title: "Model Name Already Exists",
      description: "You already have a model with this name. Please choose a different name.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    setIsModelNameInvalid(true);
    return;
  } else {
      setIsModelNameInvalid(false);
    }
  
    if (!files || files.length === 0) {
      toast({
        title: "No Images Uploaded",
        description: "Please upload at least one image.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  
    const totalSizeInMB = files.reduce((acc, file) => acc + file.file.size / (1024 * 1024), 0);
    if (totalSizeInMB > 100) {
      toast({
        title: "File Size Limit Exceeded",
        description: "The total size of your images exceeds the 100MB limit.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  
    if (!termsAccepted) {
      toast({
        title: "Terms Not Accepted",
        description: "You must accept the terms and conditions to proceed.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  
    if (profile?.credit! < 50) {
      toast({
        title: "Not Enough Credit",
        description: "You don't have enough credit to train a model.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  
    try {
      const formData = new FormData();
      const modelData = {
        display_name: modelName,
        username: modelName.toLowerCase().replace(/[^a-z0-9]/g, ""),
        model_category_name: modelCategoryName,
        isFlux: useFlux, // Add isFlux parameter based on the checkbox selection
      };
      const tuneData = {
        style_name: tuneId === "593727" ? "anime" : tuneId === "733097" ? "digital" : "realistic",
        tune_id: tuneId,
      };
  
      formData.append("model_data", JSON.stringify(modelData));
      formData.append("tune_data", JSON.stringify(tuneData));
  
      if (useFlux) {
        // FLUX Case: Append only 'images'
        files.forEach((file: any) => {
          formData.append("images", file.file);
        });
      } else {
        // Non-FLUX Case: Append model_picture, model_thumbnail, and train_images
        formData.append("model_picture", files[0].file);
        formData.append("model_thumbnail", files[0].file);
        files.forEach((file: any) => {
          formData.append("train_images", file.file);
        });
      }
  
      setIsLoading(true);
  
      const endpoint = useFlux ? POST_TRAIN_USER_MODEL_FLUX : POST_TRAIN_USER_MODEL;
  
     
      if (useFlux) {
        setIsTraining(true);
        setShowProgressBar(true);
      
        let currentProgress = 0;
        let progressInterval: ReturnType<typeof setInterval>;
        let completeInterval: ReturnType<typeof setInterval>;
        let hasCheckedAt85 = false;
      
        // Track the model ID returned from create-flux
        let modelId: string | null = null;
      
        // Start progress bar loading (1% every 3.75s)
        progressInterval = setInterval(async () => {
          currentProgress += 1;
          setProgress(currentProgress);
        
          if (currentProgress === 85) {
            clearInterval(progressInterval);
            console.log("Polling status for username:", modelData.username);

            const status = await pollStatusByModelUsername(modelData.username);
          
            if (status === "completed") {
              // continue to 100% slowly
              completeInterval = setInterval(() => {
                currentProgress += 1;
                setProgress(currentProgress);
          
                if (currentProgress >= 100) {
                  clearInterval(completeInterval);
                  toast({
                    title: "Model Training Complete",
                    description: "Your FLUX model has been successfully trained!",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                  });
                  setShowProgressBar(false);
                  setProgress(0);
                  setIsTraining(false);
                }
              }, 1000); // 1% every 1 second
            } else {
              // failed
              toast({
                title: "Model Training Failed",
                description: "The model did not finish training. Please try again later.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
              setShowProgressBar(false);
              setProgress(0);
              setIsTraining(false);
            }
          }          
        }, 3750);
        
      
        // 1. Submit form
        const response = await fetch(endpoint, { method: "POST", body: formData }, true, 60000);
      
        // 2. Get model ID and hold it for polling later
        if (response?._id) {
          modelId = response._id;
        }
      
        // 3. Polling function to check status at 85%
        const checkStatusAt85 = async (modelId: string) => {
          try {
            const model = await fetch(GET_USER_MODEL_BY_ID(modelId));
            if (model?.statusFlux === "completed") {
              // ✅ Continue from 85 → 100 (1% per sec)
              completeInterval = setInterval(() => {
                currentProgress += 1;
                setProgress(currentProgress);
                if (currentProgress >= 100) {
                  clearInterval(completeInterval!);
                  setTimeout(() => {
                    toast({
                      title: "Model Training Complete",
                      description: "Your FLUX model has been successfully trained!",
                      status: "success",
                      duration: 5000,
                      isClosable: true,
                    });
                    setShowProgressBar(false);
                    setProgress(0);
                    setIsTraining(false);
                  }, 400);
                }
              }, 1000);
            } else {
              // ❌ Fail state
              setShowProgressBar(false);
              setProgress(0);
              setIsTraining(false);
              toast({
                title: "Model Training Failed",
                description: "The model did not finish training. Please try again later.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          } catch (err) {
            console.error("Error checking model status at 85%:", err);
            setShowProgressBar(false);
            setProgress(0);
            setIsTraining(false);
            toast({
              title: "Model Training Error",
              description: "Something went wrong while checking training status.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        };
      }          
       else {
        // ✅ Non-FLUX branch — keep it separate
        const response = await fetch(endpoint, { method: "POST", body: formData }, true, 60000);
        console.log("Model creation successful", response);
        queryClient.invalidateQueries({ queryKey: ["models"] });
        gtag("event", "training_success", { event_category: "Model Training", event_label: "Success" });
        router.push("/train/success");
      }
      
      if (!useFlux) {
        router.push("/train/success");
      }
    } catch (err: unknown) {
      const error = err as { detail?: string };
      console.error("Error in model creation or tuning:", error);
    
      const message = error.detail?.toLowerCase() || "";
    
      if (!message.includes("timeout") && !message.includes("timed out")) {
        toast({
          title: "Upload Failed",
          description: error.detail || "An unexpected error occurred",
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      }
    }
     finally {
      setIsLoading(false);
    }
  };
  
  
  return (
    <Flex direction="column">
      <Jumbotron height="100%">
        <Flex h="280px" textAlign="center" direction="column" align="center">
          <Text
            mt="60px"
            fontSize={{ base: "38px", lg: "58px" }}
            fontWeight="semibold"
            style={{
              background: "linear-gradient(to right,  #FFE1FF 40%, #A4DBEE 60%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              MozBackgroundClip: "text",
            }}
            w={{ base: "310px", lg: "750px" }}
            bg="button.500"
            _hover={{ opacity: 0.9, bg: "button.500" }}
          >
            Create your own model
          </Text>
        </Flex>
      </Jumbotron>
      <Flex direction="column" px={["35px", "35px", "40px", "60px", "60px", "15%"]}>
        <Flex direction={{ base: "column", lg: "row" }} justify="space-between">
          <Flex direction="column">
            <Text>Model Name</Text>
            <Input
              ref={modelNameRef}
              w={{ base: "100%", lg: "430px" }}
              color="black"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              bg="white"
              placeholder="This will be the name of your model"
              isInvalid={isModelNameInvalid}
              errorBorderColor="red.500"
            />
            <Text mt="26px" fontWeight={700}>
              Gender
            </Text>
            <RadioGroup mt="20px" onChange={setModelCategoryName} value={modelCategoryName}>
              <Stack spacing={4}>
                <Radio value="woman">Female</Radio>
                <Radio value="man">Male</Radio>
              </Stack>
            </RadioGroup>
  
            <Text mt="26px" fontWeight={700}>
              Model Type (type of your photos)
            </Text>
            <RadioGroup
                mt="20px"
                onChange={(value) => {
                  setTuneId(value);
                  setUseFlux(value === "flux"); // If Flux is chosen, set useFlux to true
                }}
                value={useFlux ? "flux" : tuneId} // Use "flux" when Flux is selected
              >
                <Stack spacing={4}>
                  {/* Flux Option */}
                  <Box>
                    <Radio value="flux" colorScheme="purple">
                      <Text fontWeight="bold" color="#E20FCE" fontSize="sm">
                        🌟 NEW! FLUX
                      </Text>
                    </Radio>
                    {useFlux && (
                      <Box
                        mt="6px"
                        pl="24px" /* Align description text under the radio button */
                        color="gray.400"
                        fontSize="sm"
                      >
                        FLUX offers stunning realism, versatile outputs, flawless hands & easy prompting!
                      </Box>
                    )}
                  </Box>

                <Radio value="1104607">Realistic Professional</Radio>
                <Radio value="1034743">Realistic Amateur</Radio>
                <Radio value="593727">Anime</Radio>
                <Radio value="733097">Digital</Radio>
              </Stack>
            </RadioGroup>

            <Text fontWeight={700} mt="50px">
              Upload your images
            </Text>
            <Text>⚠️ Your photos will be deleted immediately from our servers after training.</Text>
            <Text>⚠️ No refunds for poorly submitted photos. Read the instructions carefully.</Text>
            <Text>
              ⚠️ Current processing time: ≈ 18 minutes. We will notify you when your AI model is ready.{" "}
            </Text>
          </Flex>
          <Flex direction="column">
            <Text mt={{ base: "30px", lg: 0 }} fontSize="18px" fontWeight={700}>
              Choosing Images - ATTENTION!
            </Text>
            <Text mt="20px">✅ Different locations, backgrounds and angles works best</Text>
            <Text>✅ Use only good quality photos {`>`}5512x512px</Text>
            <Text>✅ Closeups, mid-shots and full body variations</Text>
            <Text mt="20px">❌ No sunglasses</Text>
            <Text>❌ No other people in the photos</Text>
            <Text>❌ Avoid harsh shadows or beauty filters</Text>
            <AspectRatio maxW="560px" ratio={16 / 9} mt="20px">
              <iframe
                src="https://www.youtube.com/embed/pSRmSpdwY0Q"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </AspectRatio>
          </Flex>
        </Flex>
        <Dropzone files={files} setFiles={setFiles}  useFlux={useFlux}  />{" "}
        <GoogleForm isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
  
        {/* New Text Component added here */}
        <Text color="yellow.500" mt="20px" fontWeight="bold">
          ⚠️  All uploaded photos will be reviewed automatically and manually.
          If you violate our terms or upload photos without rights, your training will be unsuccessful, and your account may be banned.
        </Text>
        {showProgressBar ? (
          <Box
            w="100%"
            bg="#858585" // Static background color when not filled
            borderRadius="8px"
            mt="30px"
            position="relative"
            h="40px"
            overflow="hidden" // Prevent gradient overflow
          >
            {/* Progress Bar */}
            <Box
              position="absolute"
              top="0"
              left="0"
              h="100%"
              bgGradient="linear(to-r, #007FFF, #FF00C7)"
              width={`${progress}%`}
              transition="width 2.1s ease-in-out"
            />

            {/* Centered Progress Text */}
            <Box
              position="absolute"
              top="0"
              left="0"
              h="100%"
              w="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="white"
              fontWeight="bold"
              fontSize="16px"
              zIndex="1" // Ensures text stays above progress bar
            >
              Training model... {progress}%
            </Box>
          </Box>
        ) : (
          <Box w="100%" mt="30px">
          <Button
            _hover={{
              bgGradient: "linear(to-r, #007FFF, #FF00C7)",
              opacity: "0.8",
            }}
            w="100%" // Make button extend full width
            color="white"
            bgGradient="linear(to-r, #007FFF, #FF00C7)"
            onClick={handleModelSubmit}
            isLoading={isLoading}
            loadingText="Training..."
          >
            {isLoading ? <Spinner /> : "Train model (50 credits)"}
          </Button>
        </Box>
        )}
        {/* Blocking Modal */}
        <Modal
          isOpen={showBlockingModal}
          onClose={() => {
            console.log("Closing modal..."); // Debugging output
            setShowBlockingModal(false);
          }}
          isCentered
          closeOnOverlayClick={true} // Clicking outside closes the modal
          motionPreset="slideInBottom" // Optional: Add a smooth close animation
        >
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader fontWeight="bold">Training in Progress</ModalHeader>
            <ModalCloseButton color="white" /> {/* The cross button */}
            <ModalBody>
              <Text>Please wait until the training is complete!</Text>
            </ModalBody>
          </ModalContent>
        </Modal>

        
        {/* Terms and Conditions - Extended */}
        <Box w="100%" mt="20px">
          <Flex align="center">
            <Checkbox
              isChecked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              colorScheme="blue"
            />
            <Text ml="10px" w="100%">
              I agree to the{" "}
              <span style={{ textDecoration: "underline" }}>
                <a href="/terms-and-conditions">terms & conditions</a>
              </span>{" "}
              and I certify that the photos I’m submitting belong to me and I have the rights to use them.
            </Text>
          </Flex>
        </Box>
      </Flex>
  
      <CustomAccordion />
    </Flex>
  );  
};

const CustomAccordion = () => {
  const faqData = [
    {
      question: "What is DeepMode and how does it work?",
      answer:
        "DeepMode is a Generative AI platform that helps creators make their AI clones. Creators can generate beautiful AI photos, whether realistic or non-realistic, with unlimited freedom. With DeepMode, creators can unleash their imagination and create unique photos with unlimited freedom.",
    },
    {
      question: "How much does DeepMode cost?",
      answer: (
        <Text>
          DeepMode offers various top-up plans: Check{" "}
          <Link style={{ textDecoration: "underline" }} href="/payments">
            this page
          </Link>{" "}
          for more details.
        </Text>
      ),
    },
    {
      question: "Can I try DeepMode for free?",
      answer:
        "Yes, every new user automatically gets 20 credits. However, to unlock training an AI model you will need to top-up your balance.",
    },
    {
      question: "Are my DeepMode creations private?",
      answer:
        "Yes, your creations on DeepMode are kept private and only you can view them. You also have the option to delete specific models or images you don’t like.",
    },
    {
      question: "Does DeepMode store my uploaded images?",
      answer:
        "No, your uploaded photos are deleted immediately from our servers after training. In addition, the Model itself is being deleted from our servers after 30 days. If you wish to extend the model storage, you can do so.",
    },
    {
      question: "Can I use my creations anywhere?",
      answer: "Yes. Your generated images are yours to use on any other platform.",
    },
    {
      question: "How is DeepMode different from Midjourney, Stable Diffusion, or Dall-E?",
      answer:
        "Unlike other AI platforms such as Midjourney, Stable Diffusion, or DALL-E, DeepMode stands out for its exclusive focus on the generation of AI clones. DeepMode is committed to providing a fully uncensored experience, allowing users unlimited creative freedom without any content restrictions.",
    },
    {
      question: "Does DeepMode run on the Cloud?",
      answer:
        "Yes, DeepMode runs in the cloud. This means that you can use the platform and view your generations across various devices.",
    },
    {
      question: "Does DeepMode offer an Affiliate program?",
      answer:
        "Absolutely! Become a partner and earn cash rewards every month. Find out more about our Affiliate Program.", 
    },
    {
      question: "Can I create videos with DeepMode?",
      answer:
        "Currently, video generation is not supported. However, we’re working on this feature and plan to introduce it soon.",
    },
    {
      question: "Can I get a refund?",
      answer:
        "We offer refunds for purchases made within the last 7 days, provided no credits have been used. If you believe you’re eligible for a refund, please contact us here.", 
    },
    {
      question: "How do I get in touch with the DeepMode team?",
      answer: (
        <Text>
          We usually hangout on{" "}
          <a style={{ textDecoration: "underline" }} href="https://discord.gg/CMwtccKthy">
            Discord
          </a>
          . You can also reach out to us via{" "}
          <a style={{ textDecoration: "underline" }} href="mailto:info@deepmode.com">
            e-mail
          </a>
          .
        </Text>
      ),
    },
  ];

  return (
    <Flex
      mt="250px"
      mb="150px"
      px={["35px", "35px", "40px", "60px", "60px", "15%"]}
      direction="column"
    >
      <Text fontSize="40px" fontWeight="semibold">
        FAQ
      </Text>
      <Accordion mt="10px" allowToggle>
        {faqData.map((item, index) => (
          <AccordionItem key={index}>
            {({ isExpanded }) => (
              <>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {item.question}
                  </Box>
                  {isExpanded ? <MinusIcon fontSize="12px" /> : <AddIcon fontSize="12px" />}
                </AccordionButton>
                <AccordionPanel pb={4}>{item.answer}</AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}
      </Accordion>
    </Flex>
  );
};

export default CreateYourModel;
